import { Checkbox } from '@tencent/udesign/dist';
import classNames from 'classnames';
import React from 'react';
import { getRowPackageLayout } from '.';
import { getColumnWidth } from '../libs';
import { getStickyClassName } from '../libs/basic';
import { ITableRowSelection, TableRowSelectionAction } from '../model/table';
import { CustomPopover } from '../../custom-popover';
import { isBoolean } from 'lodash';

interface IProps<T> {
  readonly rowSelection: ITableRowSelection<T>;
  readonly rowIndex: number; // -1 表示头部，body 区域则是从0 - N，如果有分页则是 N1 ~ NN
  readonly checked: TableRowSelectionAction;
  readonly rowKey: string;
  readonly className?: string;

  // 头部全选、内容部分选择，有两种实现方式，一种是根据表格索引（也就是 index）来计算。另一种使用数据的唯一值 key 来计算选中。
  // 目前我们采用下标计算方式，如下：
  // 表格全部数据的长度（目前不考虑前端分页的数据，只适配了后端分页或者不分页）
  readonly data: ReadonlyArray<T>;
  readonly fullData: ReadonlyArray<T>;
  readonly record?: T;
}
export function RenderRowSelection<T>(props: IProps<T>) {
  const { rowSelection, record, className } = props;

  const isHeader = props.rowIndex === -1;

  const style = Object.assign(getColumnWidth(rowSelection.columnWidth));
  const innerClassName = classNames(
    getStickyClassName(rowSelection.fixed, isHeader),
    className,
    'auto-table-row-selection-td'
  );

  const checked = props.checked === TableRowSelectionAction.checked;
  const indeterminate = props.checked === TableRowSelectionAction.indeterminate;

  const onChangeHeader = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const { checked } = event.target;
    const keys = checked
      ? props.fullData.map((item) => {
          const row = item as Record<string, any>;
          const rowKeyValue = row[props.rowKey] as string;
          return rowKeyValue;
        })
      : [];
    rowSelection.onChange(
      keys,
      checked ? props.fullData : [],
      undefined,
      checked,
      true
    );
  };

  const onChangeBody = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const { checked } = event.target;
    const row = record as Record<string, any>;
    const rowKeyValue = row[props.rowKey];
    let keys = rowSelection.selectedRowKeys.slice() as any[];
    if (checked) {
      keys.push(rowKeyValue);
    } else {
      keys = keys.filter((key) => key !== rowKeyValue);
    }

    const selectedRows = props.fullData.filter((row) => {
      return keys.includes((row as Record<string, any>)[props.rowKey]);
    });

    rowSelection.onChange(keys, selectedRows, record, checked, false);
  };

  const renderCheckbox = () => {
    const checkBoxProps = props.rowSelection.getCheckboxProps?.(record) || {};
    if (isHeader) {
      if (props.rowSelection.disabledSelectAll) {
        return null;
      }
      return (
        <Checkbox
          checked={
            isBoolean(checkBoxProps.checked) ? checkBoxProps.checked : checked
          }
          className={classNames('pointer-all', checkBoxProps.className)}
          indeterminate={
            isBoolean(checkBoxProps.indeterminate)
              ? checkBoxProps.indeterminate
              : indeterminate
          }
          onChange={onChangeHeader}
          disabled={checkBoxProps.disabled}
        />
      );
    }

    const checkboxEle = (
      <Checkbox
        className={classNames('pointer-all', checkBoxProps.className)}
        checked={checked}
        onChange={onChangeBody}
        disabled={checkBoxProps.disabled}
      />
    );

    if (checkBoxProps.tips) {
      return (
        <CustomPopover content={checkBoxProps.tips}>
          <div className="auto-table-checkbox-with-tip">{checkboxEle}</div>
        </CustomPopover>
      );
    }
    return checkboxEle;
  };

  if (isHeader) {
    return (
      <th className={innerClassName} style={style}>
        {getRowPackageLayout(renderCheckbox())}
      </th>
    );
  }

  const cellProps = record
    ? rowSelection.onCell?.(record, props.rowIndex) || {}
    : {};

  if (cellProps.rowSpan === 0 || cellProps.rowSpan === 0) {
    return null;
  }

  return (
    <td className={classNames(innerClassName)} style={style} {...cellProps}>
      {getRowPackageLayout(renderCheckbox())}
    </td>
  );
}
